import { useEffect, useState } from 'react'
import { useAuthStore, usePlayerStore } from '../stores'
import { useTrialStore } from '../stores/useTrialStore'
import { Calendar, MapPin, CheckCircle2, XCircle, Clock, Building2, AlertCircle, Timer, Phone } from 'lucide-react'

function PlayerTrialInvitations() {
  const { user } = useAuthStore()
  const { players } = usePlayerStore()
  const { invitations, loading, error, fetchInvitations, respondToInvitation } = useTrialStore()
  const [respondingId, setRespondingId] = useState<string | null>(null)

  const currentPlayer = players.find(p =>
    p.email?.toLowerCase() === user?.email?.toLowerCase() ||
    p.name?.toLowerCase() === user?.name?.toLowerCase()
  )

  useEffect(() => {
    if (currentPlayer?.id) {
      fetchInvitations(currentPlayer.id)
    }
  }, [currentPlayer?.id, fetchInvitations])

  const handleRespond = async (id: string, status: 'accepted' | 'declined') => {
    setRespondingId(id)
    try {
      await respondToInvitation(id, status)
    } catch {
      // error handled in store
    } finally {
      setRespondingId(null)
    }
  }

  const pending = invitations.filter(i => i.status === 'pending')
  const responded = invitations.filter(i => i.status !== 'pending')

  const statusConfig = {
    pending: { label: 'Pending', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
    accepted: { label: 'Accepted', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
    declined: { label: 'Declined', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  }

  if (loading) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-400 text-sm">Loading trial invitations...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-3xl border border-red-500/20 bg-red-500/5 p-8 text-center">
        <AlertCircle className="mx-auto text-red-400 mb-4" size={48} />
        <h3 className="text-lg font-semibold text-white">Error</h3>
        <p className="text-red-300 mt-2">{error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Pending Invitations */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-yellow-400" />
          <h3 className="text-xl font-bold text-white">Pending Invitations</h3>
          {pending.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-yellow-500/20 text-yellow-400 rounded-full">
              {pending.length}
            </span>
          )}
        </div>
        {pending.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
            <Calendar className="mx-auto text-gray-500 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-white">No Pending Invitations</h3>
            <p className="text-gray-400 mt-2">You have no pending trial invitations at the moment.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pending.map((inv) => {
              const config = statusConfig.pending
              return (
                <div key={inv.id} className="bg-[#161b22] border border-gray-800 rounded-3xl p-6 hover:border-yellow-500/20 transition">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-yellow-500/10 flex items-center justify-center">
                        <Building2 size={20} className="text-yellow-400" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white">{inv.club_name}</h4>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded-full ${config.bg} ${config.color} border ${config.border}`}>
                          <config.icon size={10} />
                          {config.label}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <Calendar size={14} className="text-gray-500" />
                      <span>{new Date(inv.trial_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-300">
                      <MapPin size={14} className="text-gray-500" />
                      <span>{inv.venue}</span>
                    </div>
                    {inv.duration && (
                      <div className="flex items-center gap-2 text-sm text-gray-300">
                        <Timer size={14} className="text-gray-500" />
                        <span>{inv.duration}</span>
                      </div>
                    )}
                    {inv.contact_info && (
                      <div className="flex items-center gap-2 text-sm text-gray-300">
                        <Phone size={14} className="text-gray-500" />
                        <span>{inv.contact_info}</span>
                      </div>
                    )}
                  </div>

                  {inv.notes && (
                    <p className="text-sm text-gray-400 mb-4 bg-[#0d1117] rounded-xl p-3 border border-gray-800">{inv.notes}</p>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleRespond(inv.id, 'accepted')}
                      disabled={respondingId === inv.id}
                      className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-500 rounded-xl text-white text-sm font-bold transition disabled:opacity-50"
                    >
                      <CheckCircle2 size={16} />
                      {respondingId === inv.id ? 'Responding...' : 'Accept'}
                    </button>
                    <button
                      onClick={() => handleRespond(inv.id, 'declined')}
                      disabled={respondingId === inv.id}
                      className="flex items-center gap-2 px-5 py-2.5 bg-red-900/30 hover:bg-red-900/50 border border-red-900/50 rounded-xl text-red-400 text-sm font-bold transition disabled:opacity-50"
                    >
                      <XCircle size={16} />
                      {respondingId === inv.id ? 'Responding...' : 'Decline'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Responded Invitations */}
      {responded.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 size={18} className="text-gray-500" />
            <h3 className="text-xl font-bold text-white">Past Invitations</h3>
          </div>
          <div className="space-y-3">
            {responded.map((inv) => {
              const config = statusConfig[inv.status]
              return (
                <div key={inv.id} className="bg-[#161b22] border border-gray-800 rounded-2xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center`}>
                      <config.icon size={16} className={config.color} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">{inv.club_name}</p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-widest">{new Date(inv.trial_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded-full ${config.bg} ${config.color}`}>
                    {config.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default PlayerTrialInvitations
